export type AgentRunStage =
  | 'planning'
  | 'clarification'
  | 'retrieval'
  | 'research'
  | 'drafting'
  | 'reviewing'
  | 'revision'
  | 'approval'
  | 'persistence'
  | 'completed'
  | 'failed';

export type AgentRunStageStatus = 'pending' | 'active' | 'completed' | 'failed';

export type AgentRunDisplayStatus = 'queued' | 'running' | 'needs_attention' | 'completed' | 'failed';

export type AgentRunDisplayStage = {
  id: AgentRunStage;
  label: string;
  status: AgentRunStageStatus;
};

export type AgentRunDisplayToolCall = {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  summary?: string;
  stage?: AgentRunStage;
};

export type AgentRunDisplay = {
  title: string;
  status: AgentRunDisplayStatus;
  statusLabel: string;
  currentStage: AgentRunStage;
  runId?: string;
  threadId?: string;
  summary?: string;
  stages: AgentRunDisplayStage[];
  toolCalls: AgentRunDisplayToolCall[];
};

type AgentRunDisplayBuildInput = {
  message?: unknown;
  meta?: unknown;
};

const STAGE_ORDER: AgentRunStage[] = [
  'planning',
  'clarification',
  'retrieval',
  'research',
  'drafting',
  'reviewing',
  'revision',
  'approval',
  'persistence',
  'completed',
  'failed',
];

const OPTIONAL_STAGES = new Set<AgentRunStage>([
  'clarification',
  'retrieval',
  'research',
  'reviewing',
  'revision',
  'approval',
  'persistence',
]);

const STAGE_LABELS: Record<AgentRunStage, string> = {
  planning: 'Planning',
  clarification: 'Clarifying',
  retrieval: 'Retrieving',
  research: 'Researching',
  drafting: 'Drafting',
  reviewing: 'Reviewing',
  revision: 'Revising',
  approval: 'Approval',
  persistence: 'Saving',
  completed: 'Completed',
  failed: 'Failed',
};

const TOOL_LABELS: Record<string, string> = {
  retrieval: 'Retrieval',
  rag: 'Retrieval',
  research: 'Research',
  researcher: 'Research',
  fact_checker: 'Fact check',
  fact_check: 'Fact check',
  claim_check: 'Claim check',
  quote_check: 'Quote check',
  citation_packaging: 'Citation packaging',
  wire_readiness: 'Wire readiness',
  reviewer_pass: 'Reviewer pass',
  approval_gate: 'Approval gate',
  insights_exec: 'Insights',
  seo_optimize: 'SEO review',
  runtime_tool: 'Tool run',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function pickString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const normalized = normalizeString(value);
    if (normalized) return normalized;
  }
  return undefined;
}

function normalizeKey(value: unknown): string {
  return normalizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

function humanizeIdentifier(value: string): string {
  const key = normalizeKey(value)
    .replace(/(^|_)node$/g, '')
    .replace(/_node$/g, '')
    .replace(/_tool$/g, '')
    .replace(/_agent$/g, '');
  if (!key) return 'Tool';
  return key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getAgentRunStageLabel(stage: AgentRunStage): string {
  return STAGE_LABELS[stage];
}

export function mapAgentRunStage(value: unknown): AgentRunStage | null {
  const key = normalizeKey(value);
  if (!key) return null;

  if (/(failed|failure|error|expired|aborted|cancelled|canceled)/.test(key)) return 'failed';
  if (/(complete|completed|done|success|succeeded|finished)/.test(key)) return 'completed';
  if (/(clarif|follow_up|needs_input|needs_details)/.test(key)) return 'clarification';
  if (/(persist|persistence|save|saving|saved|store|storage|firestore|post_run)/.test(key)) return 'persistence';
  if (/(approval|approve|approved|gate|pending_review|human_review)/.test(key)) return 'approval';
  if (/(redraft|revision|revise|refine|autofix|auto_fix|edit|editor|format|formatting)/.test(key)) return 'revision';
  if (/(review|qa|quality|policy|claim|quote|citation|wire|structure|headline|consistency|validate|validation|fact_check)/.test(key)) return 'reviewing';
  if (/(draft|writer|writing|generate|generation|compose|content|text_message)/.test(key)) return 'drafting';
  if (/(research|researcher|source|web|news|browse|search)/.test(key)) return 'research';
  if (/(retriev|rag|context|enrich|memory|knowledge)/.test(key)) return 'retrieval';
  if (/(plan|planning|route|router|dispatch|validate|validating|request|normalize|queued|pending|handoff|select)/.test(key)) return 'planning';
  if (/(running|in_progress|processing|finalizing)/.test(key)) return 'drafting';

  return null;
}

function mapPendingStatus(value: unknown): {
  status?: AgentRunDisplayStatus;
  stage?: AgentRunStage;
} {
  const key = normalizeKey(value);
  if (!key) return {};
  if (key === 'queued' || key === 'pending') return { status: 'queued', stage: 'planning' };
  if (key === 'needs_clarification' || key === 'clarification_needed') {
    return { status: 'needs_attention', stage: 'clarification' };
  }
  if (key === 'pending_review' || key === 'needs_review' || key === 'approval_needed' || key === 'needs_revision' || key === 'changes_requested') {
    return { status: 'needs_attention', stage: 'approval' };
  }
  if (key === 'completed' || key === 'complete' || key === 'done') {
    return { status: 'completed', stage: 'completed' };
  }
  if (key === 'failed' || key === 'error' || key === 'expired') {
    return { status: 'failed', stage: 'failed' };
  }
  if (key === 'in_progress' || key === 'running' || key === 'processing') {
    return { status: 'running', stage: 'drafting' };
  }
  return {};
}

function mapRuntimeToolStage(state: Record<string, unknown> | null): AgentRunStage | null {
  if (!state) return null;
  const statusStage = mapAgentRunStage(state.status);
  if (statusStage === 'failed' || statusStage === 'completed') return statusStage;

  const stage = normalizeKey(state.stage);
  if (stage === 'validating' || stage === 'dispatching') return 'planning';
  if (stage === 'finalizing') return 'persistence';

  const toolStage = mapAgentRunStage(state.toolId) || mapAgentRunStage(state.label);
  return mapAgentRunStage(stage) || toolStage || null;
}

function normalizeStatus(input: {
  pendingStatus?: unknown;
  runtimeState?: Record<string, unknown> | null;
  activityStage?: AgentRunStage | null;
  hasRunSignals: boolean;
  isLoading?: unknown;
}): AgentRunDisplayStatus {
  const pending = mapPendingStatus(input.pendingStatus);
  if (pending.status) return pending.status;

  const runtimeStatus = normalizeKey(input.runtimeState?.status);
  if (runtimeStatus === 'failed' || runtimeStatus === 'error') return 'failed';
  if (runtimeStatus === 'completed' || runtimeStatus === 'complete') return 'completed';
  if (runtimeStatus === 'pending') return 'queued';
  if (runtimeStatus === 'running') return 'running';

  if (input.activityStage === 'failed') return 'failed';
  if (input.activityStage === 'completed') return 'completed';
  if (input.isLoading === true) return 'running';
  return input.hasRunSignals ? 'completed' : 'running';
}

function statusLabel(status: AgentRunDisplayStatus): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'needs_attention':
      return 'Needs clarification';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return 'In progress';
  }
}

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((entry) => normalizeString(entry)).filter(Boolean)
    : [];
}

function normalizeArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function collectActivity(...records: Array<Record<string, unknown> | null>): Record<string, unknown> | null {
  for (const record of records) {
    const activity = asRecord(record?.activity);
    if (activity) return activity;
    const agui = asRecord(record?.agui) || asRecord(record?.agUi) || asRecord(record?.ag_ui);
    const aguiActivity = asRecord(agui?.activity);
    if (aguiActivity) return aguiActivity;
  }
  return null;
}

function collectToolCalls(...records: Array<Record<string, unknown> | null>): unknown[] {
  for (const record of records) {
    if (Array.isArray(record?.toolCalls)) return record.toolCalls;
    if (Array.isArray(record?.tool_calls)) return record.tool_calls;
    const agui = asRecord(record?.agui) || asRecord(record?.agUi) || asRecord(record?.ag_ui);
    if (Array.isArray(agui?.toolCalls)) return agui.toolCalls;
    if (Array.isArray(agui?.tool_calls)) return agui.tool_calls;
  }
  return [];
}

function toolCallStatus(entry: Record<string, unknown>): AgentRunDisplayToolCall['status'] {
  const status = normalizeKey(entry.status);
  if (status === 'failed' || status === 'error') return 'failed';
  if (status === 'completed' || status === 'complete' || status === 'done') return 'completed';
  if (status === 'pending' || status === 'queued') return 'pending';
  return 'running';
}

function resultSummary(result: unknown): string | undefined {
  const record = asRecord(result);
  if (!record) return undefined;
  const sourcesFound = typeof record.sourcesFound === 'number'
    ? record.sourcesFound
    : typeof record.sourceCount === 'number'
      ? record.sourceCount
      : undefined;
  if (typeof sourcesFound === 'number') {
    return `${sourcesFound} source${sourcesFound === 1 ? '' : 's'} found`;
  }
  const summary = pickString(record.summary, record.message, record.status);
  return summary && summary.length <= 140 ? summary : undefined;
}

function toolDisplayName(value: unknown): string {
  const raw = normalizeString(value);
  const key = normalizeKey(raw);
  if (!raw) return 'Tool';
  if (/\s/.test(raw) && !/[_-]/.test(raw)) return raw;
  return TOOL_LABELS[key] || humanizeIdentifier(raw);
}

function normalizeToolCalls(rawToolCalls: unknown[], runtimeState: Record<string, unknown> | null): AgentRunDisplayToolCall[] {
  const calls: AgentRunDisplayToolCall[] = [];

  rawToolCalls.forEach((raw, index) => {
    const entry = asRecord(raw);
    if (!entry) return;
    const rawName = pickString(entry.toolName, entry.name, entry.tool, entry.toolId, entry.id) || 'Tool';
    const stage = mapAgentRunStage(rawName) || undefined;
    calls.push({
      id: pickString(entry.toolCallId, entry.id, entry.requestId) || `tool-${index}`,
      name: toolDisplayName(rawName),
      status: toolCallStatus(entry),
      ...(stage ? { stage } : {}),
      ...(resultSummary(entry.result) ? { summary: resultSummary(entry.result) } : {}),
    });
  });

  if (runtimeState) {
    const name = pickString(runtimeState.label, runtimeState.toolId) || 'Tool run';
    const stage = mapRuntimeToolStage(runtimeState) || mapAgentRunStage(name) || undefined;
    const status = toolCallStatus(runtimeState);
    calls.push({
      id: pickString(runtimeState.requestId, runtimeState.runId) || 'runtime-tool',
      name: toolDisplayName(name),
      status,
      ...(stage ? { stage } : {}),
      ...(pickString(runtimeState.summary, runtimeState.error) ? { summary: pickString(runtimeState.summary, runtimeState.error) } : {}),
    });
  }

  const seen = new Set<string>();
  return calls.filter((call) => {
    const key = `${call.id}:${call.name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function addStage(target: Set<AgentRunStage>, stage: AgentRunStage | null | undefined): void {
  if (stage) target.add(stage);
}

function inferMetaStages(meta: Record<string, unknown> | null, provenance: Record<string, unknown> | null): Set<AgentRunStage> {
  const stages = new Set<AgentRunStage>();
  const source = provenance || meta;
  if (!source) return stages;

  if (asRecord(source.routing) || asRecord(source.supervisor)) stages.add('planning');
  if (asRecord(source.rag) || Array.isArray(source.memoriesUsed) || Array.isArray(meta?.memories)) stages.add('retrieval');
  if (Array.isArray(source.citationsV2) || asRecord(source.knowledgePack)) stages.add('research');
  if (Array.isArray(source.policyIssues) || asRecord(source.consistency) || asRecord(source.pillars)) stages.add('reviewing');
  const autoFix = asRecord(source.autoFix);
  if (autoFix && (typeof autoFix.appliedCount === 'number' || Array.isArray(autoFix.resolved))) stages.add('revision');
  if (meta?.clarificationAck) stages.add('clarification');

  return stages;
}

function stageFromStatusText(...values: unknown[]): AgentRunStage | null {
  for (const value of values) {
    const stage = mapAgentRunStage(value);
    if (stage) return stage;
  }
  return null;
}

function buildTimeline(input: {
  status: AgentRunDisplayStatus;
  activeStage: AgentRunStage;
  seenStages: Set<AgentRunStage>;
  completedStages: Set<AgentRunStage>;
}): AgentRunDisplayStage[] {
  const include = new Set<AgentRunStage>(input.seenStages);
  const activeIndex = STAGE_ORDER.indexOf(input.activeStage);

  addStage(include, input.activeStage);
  if (input.status === 'failed') {
    include.add('failed');
  } else {
    include.add('completed');
  }

  if (!include.has('planning') && input.activeStage !== 'planning') {
    include.add('planning');
  }

  if (include.size <= 2 && !include.has('drafting') && input.status !== 'needs_attention') {
    include.add('drafting');
  }

  const ordered = STAGE_ORDER.filter((stage) => {
    if (stage === 'failed') return input.status === 'failed';
    if (stage === 'completed') return input.status !== 'failed';
    if (include.has(stage)) return true;
    if (!OPTIONAL_STAGES.has(stage)) return activeIndex > STAGE_ORDER.indexOf(stage);
    return false;
  });

  return ordered.map((stage) => {
    let status: AgentRunStageStatus = 'pending';
    if (input.status === 'failed' && stage === 'failed') {
      status = 'failed';
    } else if (input.status === 'completed') {
      status = 'completed';
    } else if (stage === input.activeStage) {
      status = 'active';
    } else if (
      input.completedStages.has(stage)
      || (activeIndex > -1 && STAGE_ORDER.indexOf(stage) < activeIndex && stage !== 'completed')
    ) {
      status = 'completed';
    }

    return {
      id: stage,
      label: STAGE_LABELS[stage],
      status,
    };
  });
}

export function buildAgentRunDisplay(input: AgentRunDisplayBuildInput): AgentRunDisplay | null {
  const message = asRecord(input.message);
  const explicitMeta = asRecord(input.meta);
  const meta = explicitMeta || asRecord(message?.meta);
  if (!meta && !message) return null;

  const provenance = asRecord(meta?.provenance);
  const runtimeTool = asRecord(meta?.runtimeTool);
  const runtimeState = asRecord(runtimeTool?.state);
  const activity = collectActivity(meta, provenance, runtimeState, message);
  const activityStage =
    mapAgentRunStage(activity?.currentStage)
    || mapAgentRunStage(activity?.currentNode);
  const pending = mapPendingStatus(meta?.pendingStatus);
  const runtimeStage = mapRuntimeToolStage(runtimeState);
  const handoff = asRecord(message?.agentHandoffData);
  const selectedAgent = asRecord(handoff?.selectedAgent);
  const statusTextStage = stageFromStatusText(handoff?.statusText, message?.content, message?.text);
  const metaStages = inferMetaStages(meta, provenance);
  const rawToolCalls = collectToolCalls(meta, provenance, runtimeState, message);
  const toolCalls = normalizeToolCalls(rawToolCalls, runtimeState);

  const runId = pickString(
    meta?.runId,
    provenance?.runId,
    asRecord(provenance?.supervisor)?.runId,
    runtimeState?.runId,
    runtimeState?.requestId,
    message?.runId,
  );
  const threadId = pickString(
    meta?.threadId,
    provenance?.threadId,
    runtimeState?.threadId,
    runtimeState?.sourceConversationId,
    message?.threadId,
  );
  const hasRunSignals = Boolean(
    runId
    || threadId
    || meta?.pendingStatus
    || runtimeTool
    || activity
    || rawToolCalls.length
    || provenance
  );

  if (!hasRunSignals) return null;

  const status = normalizeStatus({
    pendingStatus: meta?.pendingStatus,
    runtimeState,
    activityStage,
    hasRunSignals,
    isLoading: message?.isLoading,
  });

  const currentStage =
    status === 'failed'
      ? 'failed'
      : status === 'completed'
        ? 'completed'
        : status === 'needs_attention'
          ? pending.stage || activityStage || runtimeStage || statusTextStage || 'clarification'
          : activityStage
          || runtimeStage
          || pending.stage
          || statusTextStage
          || (toolCalls.find((call) => call.status === 'running')?.stage)
          || 'planning';

  const seenStages = new Set<AgentRunStage>(metaStages);
  const completedStages = new Set<AgentRunStage>();

  normalizeStringArray(activity?.pipelineStages).forEach((stage) => addStage(seenStages, mapAgentRunStage(stage)));
  normalizeStringArray(activity?.completedStages).forEach((stage) => {
    const mapped = mapAgentRunStage(stage);
    addStage(seenStages, mapped);
    addStage(completedStages, mapped);
  });
  addStage(seenStages, activityStage);
  addStage(seenStages, runtimeStage);
  addStage(seenStages, pending.stage);
  toolCalls.forEach((call) => addStage(seenStages, call.stage));

  if (status === 'completed') {
    seenStages.forEach((stage) => {
      if (stage !== 'failed') completedStages.add(stage);
    });
    completedStages.add('completed');
  }

  const title = pickString(runtimeState?.label, selectedAgent?.name)
    ? `${pickString(runtimeState?.label, selectedAgent?.name)} run`
    : 'Agent run';
  const summary = status === 'failed'
    ? pickString(runtimeTool?.error, runtimeState?.error, message?.content)
    : pickString(runtimeState?.summary, handoff?.statusText);

  // Reasoning fields are intentionally ignored; this display only surfaces observable run state.
  return {
    title,
    status,
    statusLabel: statusLabel(status),
    currentStage,
    ...(runId ? { runId } : {}),
    ...(threadId ? { threadId } : {}),
    ...(summary ? { summary } : {}),
    stages: buildTimeline({
      status,
      activeStage: currentStage,
      seenStages,
      completedStages,
    }),
    toolCalls,
  };
}

export function buildAgentRunDisplayFromMessage(message: unknown): AgentRunDisplay | null {
  return buildAgentRunDisplay({ message });
}

export function buildAgentRunDisplayFromMeta(meta: unknown): AgentRunDisplay | null {
  return buildAgentRunDisplay({ meta });
}

export function buildAgentRunDisplayFromArtifact(artifactValue: unknown): AgentRunDisplay | null {
  const artifact = asRecord(artifactValue);
  if (!artifact) return null;

  const format = asRecord(artifact.format);
  const formatPolicy = asRecord(format?.policy);
  const supervisorMeta = asRecord(artifact.supervisorMeta);
  const citations = Array.isArray(format?.citations) ? format.citations : [];
  const policyIssues = [
    ...normalizeArray(artifact.policyIssues),
    ...normalizeArray(formatPolicy?.issues),
  ];
  const hasObservableRunState = Boolean(
    artifact.runId
    || artifact.sourceConversationId
    || supervisorMeta
    || artifact.rag
    || artifact.researcher
    || artifact.consistency
    || artifact.autoFix
    || artifact.pillars
    || citations.length
    || policyIssues.length
  );

  if (!hasObservableRunState) return null;

  const approvalStatus = normalizeKey(artifact.approvalStatus);
  const pendingStatus = artifact.isLoading === true
    ? 'in_progress'
    : approvalStatus === 'pending' || approvalStatus === 'needs_revision' || approvalStatus === 'rejected'
      ? 'pending_review'
      : 'completed';

  const meta: Record<string, unknown> = {
    pendingStatus,
    ...(artifact.runId ? { runId: artifact.runId } : {}),
    ...(artifact.sourceConversationId ? { threadId: artifact.sourceConversationId } : {}),
    provenance: {
      ...(supervisorMeta ? { supervisor: supervisorMeta } : {}),
      ...(artifact.rag ? { rag: artifact.rag } : {}),
      ...(artifact.researcher ? { knowledgePack: artifact.researcher } : {}),
      ...(artifact.consistency ? { consistency: artifact.consistency } : {}),
      ...(artifact.autoFix ? { autoFix: artifact.autoFix } : {}),
      ...(artifact.pillars ? { pillars: artifact.pillars } : {}),
      ...(citations.length ? { citationsV2: citations } : {}),
      ...(policyIssues.length ? { policyIssues } : {}),
    },
  };

  const display = buildAgentRunDisplay({
    message: {
      isLoading: artifact.isLoading === true,
      content: artifact.title,
      meta,
    },
  });

  if (!display) return null;

  const title = pickString(artifact.title);
  return {
    ...display,
    title: title ? `${title.slice(0, 80)} run` : 'Artifact run',
  };
}
